import React, {useState} from 'react';
import {Moment} from 'moment';
import Select from 'react-select';
import './TopicRollupDateRangeSelect.styl';
import {TopicRollupDateRanges} from 'stores/dashboard/DashboardDateRangeStore';
import DashboardDateRangeActions from 'actions/dashboard/DasbhoardDateRangeActions';
import {DashboardDateRange} from '@store/@types/dash/DashboardDateRange';

interface TopicRollupDateRangeSelectOption {
  label: string;
  value: DashboardDateRange;
}

interface TopicRollupDateRangeSelectProps {
  startDate: Moment;
  endDate: Moment;
  topicRollupDateRanges: TopicRollupDateRanges;
}

/**
 * Date selection dropdown specifically for topic rollups, providing a limited set of options.
 *
 * Values for these options are precomputed on the backend by a nightly job, and results are cached in redis for fast
 * lookup.
 *
 * @param startDate the dashboard header start date.
 * @param endDate the dashboard header end date.
 * @param topicRollupDateRanges date ranges available for the user to select.
 * @constructor
 */
const TopicRollupDateRangeSelect: React.FC<TopicRollupDateRangeSelectProps> = ({
  startDate,
  endDate,
  topicRollupDateRanges,
}) => {
  const [options] = useState<TopicRollupDateRangeSelectOption[]>(() => {
    return Object.keys(topicRollupDateRanges).map(key => {
      return {label: key, value: topicRollupDateRanges[key]};
    });
  });

  function findOrDefault(start: Moment, end: Moment): TopicRollupDateRangeSelectOption {
    const found = options.find(
      item => item.value.startDate.isSame(start) && item.value.endDate.isSame(end)
    );
    if (!found) {
      // Should never happen. Probably an issue with the store or the functions we use to pull dates from it.
      console.error(
        "Got a date range that isn't valid for this component. Falling back to default."
      );
      return options[0];
    }
    return found;
  }

  return (
    <div className="dashboard-date-range">
      <Select
        multi={false}
        searchable={false}
        options={options}
        className="dashboard-date-select"
        onChange={option => DashboardDateRangeActions.setDateRange(option.value)}
        value={findOrDefault(startDate, endDate)}
      />
    </div>
  );
};

export default TopicRollupDateRangeSelect;
